import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import blogService from "../../services/blogService";
import { tourService } from "../../services/tourService";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import axios from "../../config/axiosInstance";
import { toast } from "react-toastify";

const BlogUpdate = () => {
  const { blogId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [blogImages, setBlogImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [titleEditor, setTitleEditor] = useState(null);
  const [contentEditor, setContentEditor] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  let userId = 0;
  if (user) {
    try {
      const decoded = jwtDecode(user);
      userId = decoded.sub || 0;
    } catch (err) {
      console.error("Lỗi khi giải mã token:", err);
      setError("Lỗi xác thực người dùng.");
    }
  }

  useEffect(() => {
    if (!blogId || isNaN(parseInt(blogId))) {
      setError("ID bài viết không hợp lệ.");
      return;
    }

    const fetchData = async () => {
      try {
        const [blogData, toursData] = await Promise.all([
          blogService.getBlogById(blogId),
          tourService.getAllTours({ page: 1, pageSize: 10 }),
        ]);

        setTitle(blogData.name || "");
        setContent(blogData.description || "");
        setSelectedTour(blogData.tourId || "");

        // Store existing images separately
        const existing = blogData.imageUrls?.map((url) => ({ url })) || [];
        setExistingImages(existing);
        setBlogImages(existing);

        setTours(toursData.data || []);

        if (titleEditor) titleEditor.setData(blogData.name || "");
        if (contentEditor) contentEditor.setData(blogData.description || "");
      } catch (err) {
        console.error("Lỗi khi lấy dữ liệu:", err);
        setError("Lỗi khi tải dữ liệu.");
      }
    };

    fetchData();
  }, [blogId, titleEditor, contentEditor]);

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (!files) return;

    const newImages = Array.from(files).map((file) => ({
      url: URL.createObjectURL(file),
      file,
      isNew: true,
    }));

    // Combine existing images with new ones
    setBlogImages([...existingImages, ...newImages]);
  };

  const removeImage = (index) => {
    const newImages = [...blogImages];
    newImages.splice(index, 1);
    setBlogImages(newImages);

    // If removing an existing image, update existingImages
    if (!blogImages[index].isNew) {
      setExistingImages(existingImages.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Strip HTML tags for validation
    const plainTitle = title.replace(/<[^>]+>/g, "").trim();
    const plainContent = content.replace(/<[^>]+>/g, "").trim();

    let errors = {};
    if (!selectedTour) errors.tour = "Vui lòng chọn Tour";
    if (!plainTitle) errors.title = "Tiêu đề không được để trống";
    if (!plainContent) errors.content = "Nội dung không được để trống";
    if (blogImages.length === 0) errors.images = "Cần ít nhất một ảnh";

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      // Only upload new images
      const newImagesToUpload = blogImages.filter((img) => img.isNew);
      const uploadedImageUrls = await Promise.all(
        newImagesToUpload.map(async (image) => {
          const formData = new FormData();
          formData.append("files", image.file);
          const response = await axios.post(
            "/uploadvu/upload-images",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          return response.data.images[0].url;
        })
      );

      // Combine existing URLs with new ones
      const existingUrls = existingImages.map((img) => img.url);
      const allImageUrls = [...existingUrls, ...uploadedImageUrls];

      const blogData = {
        Name: title,
        Description: content,
        AuthorId: userId,
        TourId: selectedTour,
        ImageUrls: allImageUrls,
      };

      await blogService.updateBlog(blogId, blogData);
      toast.success("Cập nhật blog thành công! Vui lòng chờ admin duyệt");
      navigate("/my-blog");
    } catch (err) {
      console.error("Lỗi khi cập nhật blog:", err);
      toast.error(err.response?.data?.message || "Có lỗi xảy ra khi cập nhật");
    } finally {
      setLoading(false);
    }
  };

  // Custom CSS configuration for CKEditor
  const editorConfig = {
    toolbar: [
      "heading",
      "|",
      "bulletedList",
      "numberedList",
      "bold",
      "italic",
      "|",
      "undo",
      "redo",
    ],
    contentsCss: [
      {
        content: `
          ul {
            list-style-type: disc !important;
            padding-left: 20px !important;
            display: block !important;
          }
          ol {
            list-style-type: decimal !important;
            padding-left: 20px !important;
            display: block !important;
          }
          li {
            margin-bottom: 0.5em !important;
            display: list-item !important;
          }
          .ck-content {
            font-family: Arial, sans-serif !important;
            line-height: 1.6 !important;
          }
        `,
      },
    ],
  };

  if (error) {
    return <div className="container mt-4 text-danger">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
          Cập Nhật Bài Viết
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tour Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chọn Tour
            </label>
            <select
              className={`w-full px-4 py-3 rounded-xl border ${
                validationErrors.tour ? "border-red-300" : "border-gray-300"
              } focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200`}
              value={selectedTour}
              onChange={(e) => setSelectedTour(e.target.value)}
            >
              <option value="">-- Chọn Tour --</option>
              {tours.map((tour) => (
                <option key={tour.tourId} value={tour.tourId}>
                  {tour.tourName}
                </option>
              ))}
            </select>
            {validationErrors.tour && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.tour}
              </p>
            )}
          </div>

          {/* Title Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tiêu đề
            </label>
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <CKEditor
                editor={ClassicEditor}
                data={title}
                onReady={(editor) => {
                  setTitleEditor(editor);
                  editor.setData(title);
                }}
                onChange={(event, editor) => setTitle(editor.getData())}
                config={{
                  ...editorConfig,
                  toolbar: ["heading", "|", "bold", "italic"],
                }}
              />
            </div>
            {validationErrors.title && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.title}
              </p>
            )}
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nội dung
            </label>
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <CKEditor
                editor={ClassicEditor}
                data={content}
                onReady={(editor) => {
                  setContentEditor(editor);
                  editor.setData(content);
                }}
                onChange={(event, editor) => setContent(editor.getData())}
                config={editorConfig}
              />
            </div>
            {validationErrors.content && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.content}
              </p>
            )}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hình ảnh
            </label>
            <div className="relative">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                className={`w-full px-4 py-3 rounded-xl border ${
                  validationErrors.images ? "border-red-300" : "border-gray-300"
                } file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors duration-200`}
              />
            </div>
            {validationErrors.images && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.images}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Bạn có thể chọn nhiều ảnh cùng lúc
            </p>
          </div>

          {/* Image Preview with Delete Option */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ảnh đã chọn
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {blogImages.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  Chưa có ảnh nào được chọn
                </p>
              ) : (
                blogImages.map((image, index) => (
                  <div
                    key={index}
                    className="relative group rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <img
                      src={image.url}
                      alt="Blog preview"
                      className="w-full h-40 object-cover"
                    />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center  hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                      onClick={() => removeImage(index)}
                      aria-label="Xóa ảnh"
                      style={{ zIndex: 10 }}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-400 text-white px-6 py-2 rounded-xl hover:bg-blue-500 transition-colors duration-300 flex items-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Đang xử lý...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane"></i>
                  Cập nhật bài viết
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogUpdate;
